// Data Deletion Instructions page.
// This is the URL we list in our Meta App Dashboard as the public
// "Data Deletion Instructions URL" (separate from the callback URL).
// Reviewers and end users land here when looking for how to delete
// their data from WFS Chatbot.

import Seo from '../components/Seo';

const SUPPORT_EMAIL = 'info@wavefrontstudiollc.com';

export default function DataDeletion() {
  return (
    <section className="bg-ink text-white">
      <Seo
        title="Data Deletion · WFS Chatbot"
        description="How to request deletion of your personal data from WFS Chatbot."
        path="/data-deletion"
      />
      <div className="mx-auto max-w-prose px-6 py-20 md:py-28">
        <span className="rr-tag">// privacy · data deletion</span>
        <h1 className="mt-4 text-display-md font-bold tracking-headline text-white">
          Delete your data
        </h1>
        <p className="mt-6 text-base text-ash">
          WFS Chatbot is a customer messaging platform used by businesses to
          reply to people who message them on Facebook, Instagram, WhatsApp,
          and other channels. If you messaged a business that uses our
          platform, your name, profile photo, and message history were stored
          so the business could reply.
        </p>
        <p className="mt-4 text-base text-ash">
          You have three ways to request deletion. Choose whichever fits.
        </p>

        <div className="mt-12 space-y-10">
          <Section title="1. Remove the app from your Meta account">
            <p>
              The fastest path. Open{' '}
              <ExternalLink href="https://www.facebook.com/settings?tab=business_tools">
                Facebook Settings → Business Integrations
              </ExternalLink>{' '}
              (or{' '}
              <ExternalLink href="https://www.instagram.com/accounts/manage_access/">
                Instagram → Apps and Websites
              </ExternalLink>{' '}
              for Instagram, or your WhatsApp Business Manager for WhatsApp).
              Find WFS Chatbot in the list and click Remove.
            </p>
            <p>
              When you do this, Meta sends us an automated deauthorization
              notice. We immediately invalidate the access tokens on our
              side. The business that was using our platform to talk to you
              still keeps the conversation history, since it is their
              business record, but our connection to your Meta account is
              cut.
            </p>
            <p>
              If you also want the business record itself deleted, use
              option 2 or 3.
            </p>
          </Section>

          <Section title="2. Request deletion through Meta">
            <p>
              In the same Business Integrations settings page above, click
              <em> View and edit </em> on WFS Chatbot, then choose
              <em> Remove </em> and tick the box that says
              <em> Send a Data Deletion Request</em>. Meta will forward the
              request to us and you will receive a confirmation code from
              Meta.
            </p>
            <p>
              We process the request automatically: every contact record
              attached to your Meta user ID is anonymized, message bodies
              are redacted, and our memory of you is cleared. You can check
              progress at the URL Meta gives you.
            </p>
          </Section>

          <Section title="3. Email us directly">
            <p>
              Send a deletion request to{' '}
              <a
                className="text-signal underline"
                href={`mailto:${SUPPORT_EMAIL}?subject=Data%20deletion%20request`}
              >
                {SUPPORT_EMAIL}
              </a>
              . Include the email, phone number, or social handle you used
              to message a business so we can find your records.
            </p>
            <p>
              We respond within 5 business days. If we cannot find any data
              tied to the identifiers you give us, we tell you that too.
            </p>
          </Section>

          <Section title="What we delete">
            <ul className="list-disc space-y-2 pl-5">
              <li>Contact record (name, profile photo, email, phone — anonymized)</li>
              <li>Message history (content redacted)</li>
              <li>AI memory and extracted entities</li>
              <li>Lead qualification records</li>
              <li>Chatbot session logs</li>
            </ul>
          </Section>

          <Section title="What stays">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                Conversation row (anonymized) — kept so the business that
                served you has an accurate count of contacts handled.
              </li>
              <li>
                Aggregate analytics (no personal identifiers).
              </li>
              <li>
                Backups, for up to 30 days, after which they roll off.
              </li>
            </ul>
          </Section>
        </div>

        <div className="mt-16 h-px bg-ink-3" aria-hidden />
        <p className="mt-6 text-sm text-ash">
          Questions or trouble?{' '}
          <a className="text-signal underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </div>
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-lg font-semibold tracking-headline text-white">{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-ash">{children}</div>
    </div>
  );
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      className="text-signal underline"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}
